import React, { useState, useEffect, useCallback } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { api } from './services/api';

// Import types
import { Alert, Tenant, IncidentMetrics } from './types';

// Import pages
import { DashboardPage, AlertsPage, AlertDetailsPage, SettingsPage, AutoFixPage } from './pages';

// Constants
const PENDING_DEEP_LINK_KEY = 'pendingDeepLink';
const DASHBOARD_PAGE = 'dashboard';
const ALERTS_PAGE = 'alerts';
const ALERT_DETAILS_PAGE = 'alert-details';
const SETTINGS_PAGE = 'settings';

const ForesightApp = () => {
  const {
    isAuthenticated,
    isLoading,
    loginWithRedirect,
    logout,
    getAccessTokenSilently,
    user
  } = useAuth0();

  const [currentTenant, setCurrentTenant] = useState<Tenant | null>(null);
  const [currentPage, setCurrentPage] = useState<string>(DASHBOARD_PAGE);
  const [alertsLoading, setAlertsLoading] = useState<boolean>(false);
  const [selectedAlert, setSelectedAlert] = useState<Alert | null>(null);
  const [alertAnalysis, setAlertAnalysis] = useState<any>(null);
  const [analysisVersions, setAnalysisVersions] = useState<any[]>([]);
  const [selectedAnalysisVersion, setSelectedAnalysisVersion] = useState<number>(0);
  const [servicesConfig, setServicesConfig] = useState<any[]>([]);
  const [selectedAlertStatus, setSelectedAlertStatus] = useState<string | null>(null);
  const [selectedAlertPriority, setSelectedAlertPriority] = useState<string | null>(null);
  const [selectedAutoFix, setSelectedAutoFix] = useState<string | null>(null);
  const [alertSearchQuery, setAlertSearchQuery] = useState<string>('');
  const [alertPage, setAlertPage] = useState<number>(1);
  const [itemsPerPage, setItemsPerPage] = useState<number>(20);
  const [showAutoFix, setShowAutoFix] = useState<boolean>(false);

  // Alerts state - dynamically loaded from API
  const [alerts, setAlerts] = useState<Alert[]>([]);
  const [alertsPagination, setAlertsPagination] = useState<{
    current_page: number;
    total_pages: number;
    total_items: number;
    per_page: number;
    has_next: boolean;
    has_prev: boolean;
  } | null>(null);

  // Incident metrics state
  const [incidentMetrics, setIncidentMetrics] = useState<IncidentMetrics | null>(null);
  const [incidentMetricsLoading, setIncidentMetricsLoading] = useState<boolean>(false);

  // Extract tenant from Auth0 user metadata
  useEffect(() => {
    if (isAuthenticated && user) {
      // Tenant info comes from Auth0 JWT org_id
      // For now, we'll use a hardcoded tenant ID (Bazaar) until we fully implement org mapping
      setCurrentTenant({
        id: '6265adf8-53fe-4cf5-87ca-e2f8589a7447',
        name: 'Bazaar',
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
      });
    } else {
      setCurrentTenant(null);
    }
  }, [isAuthenticated, user]);

  // Fetch incident metrics from API
  const loadIncidentMetrics = useCallback(async () => {
    if (isAuthenticated && currentTenant) {
      try {
        setIncidentMetricsLoading(true);
        const token = await getAccessTokenSilently();
        const metrics = await api.getIncidentMetrics(token);
        setIncidentMetrics(metrics);
      } catch (error) {
        console.error('Failed to load incident metrics from API:', error);
        setIncidentMetrics(null);
      } finally {
        setIncidentMetricsLoading(false);
      }
    } else {
      setIncidentMetrics(null);
    }
  }, [isAuthenticated, currentTenant, getAccessTokenSilently]);

  // Fetch alerts from API
  const loadAlerts = useCallback(async (page: number = 1, perPage: number = 20) => {
    if (isAuthenticated && currentTenant) {
      try {
        setAlertsLoading(true);
        const token = await getAccessTokenSilently();
        const data = await api.getAlerts(token, page, perPage);

        // Convert API alerts to match our Alert interface
        const convertedAlerts: Alert[] = data.data.map((apiAlert: any) => ({
          id: apiAlert.id,
          created_at: apiAlert.created_at || new Date().toISOString(),
          updated_at: apiAlert.updated_at || new Date().toISOString(),
          tenant_id: apiAlert.tenant_id || currentTenant?.id || '',
          grafana_alert_id: apiAlert.grafana_alert_id || apiAlert.id,
          grafana_group_id: apiAlert.grafana_group_id || null,
          title: apiAlert.title,
          status: apiAlert.status || 'FIRING',
          severity: apiAlert.severity || 'MEDIUM',
          priority: apiAlert.priority || 1,
          starts_at: apiAlert.starts_at || apiAlert.created_at || new Date().toISOString(),
          ends_at: apiAlert.ends_at || null,
          labels: {
            service: apiAlert.service_name || apiAlert.service || 'unknown',
            environment: apiAlert.environment || 'production',
            severity: apiAlert.severity || 'MEDIUM',
            ...apiAlert.labels
          },
          annotations: {
            summary: apiAlert.summary || apiAlert.title,
            description: apiAlert.description || apiAlert.title,
            ...apiAlert.annotations
          },
          service_name: apiAlert.service_name || apiAlert.service || 'unknown',
          auto_fix_available: apiAlert.auto_fix_available || false
        }));

        setAlerts(convertedAlerts);
        setAlertsPagination(data.pagination);
      } catch (error) {
        console.error('Failed to load alerts from API:', error);
        setAlerts([]);
        setAlertsPagination(null);
      } finally {
        setAlertsLoading(false);
      }
    } else {
      setAlerts([]);
      setAlertsPagination(null);
    }
  }, [isAuthenticated, currentTenant, getAccessTokenSilently]);

  // Load service configurations
  const loadServicesConfig = useCallback(async () => {
    if (!isAuthenticated || !currentTenant) {
      return;
    }

    try {
      const token = await getAccessTokenSilently();;
      const response = await api.getConfig(token);

      if (response && response.service_configs) {
        setServicesConfig(response.service_configs);
      } else {
        // Try to set empty array as fallback
        setServicesConfig([]);
      }
    } catch (error) {
      console.error('Failed to load service configurations:', error);
      // Set empty array on error
      setServicesConfig([]);
    }
  }, [isAuthenticated, currentTenant, getAccessTokenSilently]);

  useEffect(() => {
    loadAlerts();
    loadIncidentMetrics();
    loadServicesConfig();
  }, [loadAlerts, loadIncidentMetrics, loadServicesConfig]);

  // Pagination handlers that reload alerts
  const handleSetAlertPage = (page: number) => {
    setAlertPage(page);
    loadAlerts(page, itemsPerPage);
  };

  const handleSetItemsPerPage = (perPage: number) => {
    setItemsPerPage(perPage);
    setAlertPage(1); // Reset to first page when changing items per page
    loadAlerts(1, perPage);
  };

  // Save deep link to localStorage on mount
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const alertParam = params.get('alert');

    if (alertParam) {
      localStorage.setItem(PENDING_DEEP_LINK_KEY, JSON.stringify({ alert: alertParam }));
    }
  }, []);

  // Handle deep link after login
  useEffect(() => {
    if (!isAuthenticated || !currentTenant || alerts.length === 0) return;

    const savedLink = localStorage.getItem(PENDING_DEEP_LINK_KEY);
    if (!savedLink) return;

    const handleDeepLink = async () => {
      try {
        const { alert: targetAlert } = JSON.parse(savedLink);
        const alert = alerts.find(a => a.id === targetAlert);

        if (alert) {
          setSelectedAlert(alert);
          setCurrentPage(ALERT_DETAILS_PAGE);

          // Load analysis
          const token = await getAccessTokenSilently();
          const analysis = await api.getAlertAnalysis(targetAlert, token);
          setAlertAnalysis(analysis);

          // Clear localStorage and URL
          localStorage.removeItem(PENDING_DEEP_LINK_KEY);
          window.history.replaceState({}, '', window.location.pathname);
        } else {
          console.log('❌ Alert not found:', targetAlert);
        }
      } catch (err) {
        console.error('Failed to handle deep link:', err);
        localStorage.removeItem(PENDING_DEEP_LINK_KEY);
      }
    };

    handleDeepLink();
  }, [alerts, isAuthenticated, currentTenant, getAccessTokenSilently]);


  // Load alert analysis from API
  useEffect(() => {
    const loadAnalysis = async () => {
      if (isAuthenticated && currentTenant && selectedAlert) {
        const alertId = selectedAlert.id.toString();

        try {
          const token = await getAccessTokenSilently();
          const analysisResponse = await api.getAlertAnalysis(alertId, token);

          // Extract the actual analysis data from the response
          if (analysisResponse.data && analysisResponse.data.length > 0) {
            // Sort analyses by version in descending order (latest first)
            const sortedAnalyses = analysisResponse.data.sort((a, b) => b.version - a.version);
            const latestAnalysis = sortedAnalyses[0]; // First item after sorting by version desc
            setAlertAnalysis(latestAnalysis);
            setAnalysisVersions(sortedAnalyses);
            setSelectedAnalysisVersion(0); // Default to first item (latest version)
          } else {
            // No analysis data available
            setAlertAnalysis(null);
            setAnalysisVersions([]);
            setSelectedAnalysisVersion(0);
          }
        } catch (error) {
          console.error('Failed to load alert analysis from API:', error);
          setAlertAnalysis(null);
          setAnalysisVersions([]);
          setSelectedAnalysisVersion(0);
        }
      } else {
        setAlertAnalysis(null);
        setAnalysisVersions([]);
        setSelectedAnalysisVersion(0);
      }
    };

    loadAnalysis();
  }, [selectedAlert, isAuthenticated, currentTenant, getAccessTokenSilently]);

  // Automatically redirect to Auth0 login if not authenticated
  useEffect(() => {
    if (!isLoading && !isAuthenticated) {
      loginWithRedirect();
    }
  }, [isLoading, isAuthenticated, loginWithRedirect]);

  // Calculate active alerts count
  // Use pagination data to estimate total active alerts if available
  const currentPageActiveAlerts = alerts.filter(alert => alert.status !== 'RESOLVED').length;
  const currentPageResolvedAlerts = alerts.filter(alert => alert.status === 'RESOLVED').length;
  const currentPageTotal = currentPageActiveAlerts + currentPageResolvedAlerts;

  const activeAlertsCount = alertsPagination && currentPageTotal > 0
    ? Math.round((currentPageActiveAlerts / currentPageTotal) * alertsPagination.total_items)
    : currentPageActiveAlerts;

  const handleNavigate = (page: string) => {
    setCurrentPage(page);
    setSelectedAlert(null);
  };

  const handleAlertClick = (alert: Alert): void => {
    setSelectedAlert(alert);
    setCurrentPage(ALERT_DETAILS_PAGE);
  };

  const handleLogout = () => {
    logout({
      logoutParams: {
        returnTo: window.location.origin
      }
    });
    setCurrentPage(DASHBOARD_PAGE);
    setSelectedAlert(null);
  };


  const goBackToAlerts = (): void => {
    setCurrentPage(ALERTS_PAGE);
    setSelectedAlert(null);
  };

  const goBackToAlertDetails = (): void => {
    setShowAutoFix(false);
  };

  const handleAnalysisVersionChange = (versionIndex: number): void => {
    setSelectedAnalysisVersion(versionIndex);
    // Update alertAnalysis to show the selected version
    if (analysisVersions && analysisVersions[versionIndex]) {
      setAlertAnalysis(analysisVersions[versionIndex]);
    }
  };

  const handleTriggerAnalysis = async (): Promise<void> => {
    if (!selectedAlert) return;

    try {
      const token = await getAccessTokenSilently();
      await api.triggerAnalysis(selectedAlert.id.toString(), token);

      // Reload analysis after triggering
      const analysisResponse = await api.getAlertAnalysis(selectedAlert.id.toString(), token);

      // Extract the actual analysis data from the response
      if (analysisResponse.data && analysisResponse.data.length > 0) {
        // Sort analyses by version in descending order (latest first)
        const sortedAnalyses = analysisResponse.data.sort((a, b) => b.version - a.version);
        const latestAnalysis = sortedAnalyses[0]; // First item after sorting by version desc
        setAlertAnalysis(latestAnalysis);
        setAnalysisVersions(sortedAnalyses);
        setSelectedAnalysisVersion(0); // Default to first item (latest version)
      } else {
        // No analysis data available
        setAlertAnalysis(null);
        setAnalysisVersions([]);
        setSelectedAnalysisVersion(0);
      }
    } catch (error) {
      console.error('Failed to trigger analysis:', error);
    }
  };

  // Show loading while Auth0 is initializing
  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-900">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
          <p className="mt-4 text-gray-300">Loading...</p>
        </div>
      </div>
    );
  }

  // Show nothing while redirecting to login
  if (!isAuthenticated) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-900">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
          <p className="mt-4 text-gray-300">Redirecting to login...</p>
        </div>
      </div>
    );
  }

  // AutoFix Page
  if (showAutoFix && selectedAlert) {
    return (
      <AutoFixPage
        currentPage={currentPage}
        activeAlertsCount={activeAlertsCount}
        selectedAlert={selectedAlert}
        alertAnalysis={alertAnalysis}
        onNavigate={handleNavigate}
        onLogout={handleLogout}
        onGoBackToAlertDetails={goBackToAlertDetails}
      />
    );
  }

  // Alert Details Page
  if (currentPage === 'alert-details' && selectedAlert) {
    return (
      <AlertDetailsPage
        currentPage={currentPage}
        activeAlertsCount={activeAlertsCount}
        selectedAlert={selectedAlert}
        alertAnalysis={alertAnalysis}
        analysisVersions={analysisVersions}
        selectedAnalysisVersion={selectedAnalysisVersion}
        servicesConfig={servicesConfig}
        onNavigate={handleNavigate}
        onLogout={handleLogout}
        onGoBackToAlerts={goBackToAlerts}
        onAnalysisVersionChange={handleAnalysisVersionChange}
        onTriggerAnalysis={handleTriggerAnalysis}
      />
    );
  }

  // Settings Page
  if (currentPage === SETTINGS_PAGE) {
    return (
      <SettingsPage
        currentPage={currentPage}
        activeAlertsCount={activeAlertsCount}
        tenantId={currentTenant?.id || ''}
        onNavigate={handleNavigate}
        onLogout={handleLogout}
      />
    );
  }

  // Alerts Page
  if (currentPage === 'alerts') {
    return (
      <AlertsPage
        currentPage={currentPage}
        activeAlertsCount={activeAlertsCount}
        alerts={alerts}
        alertsLoading={alertsLoading}
        alertsPagination={alertsPagination}
        selectedAlertStatus={selectedAlertStatus}
        selectedAlertPriority={selectedAlertPriority}
        selectedAutoFix={selectedAutoFix}
        alertSearchQuery={alertSearchQuery}
        alertPage={alertPage}
        itemsPerPage={itemsPerPage}
        onNavigate={handleNavigate}
        onLogout={handleLogout}
        onSetSelectedAlertStatus={setSelectedAlertStatus}
        onSetSelectedAlertPriority={setSelectedAlertPriority}
        onSetSelectedAutoFix={setSelectedAutoFix}
        onSetAlertSearchQuery={setAlertSearchQuery}
        onSetAlertPage={handleSetAlertPage}
        onSetItemsPerPage={handleSetItemsPerPage}
        onAlertClick={handleAlertClick}
      />
    );
  }

  // Dashboard Page
  return (
    <DashboardPage
      currentPage={currentPage}
      activeAlertsCount={activeAlertsCount}
      alerts={alerts}
      alertsPagination={alertsPagination}
      incidentMetrics={incidentMetrics}
      incidentMetricsLoading={incidentMetricsLoading}
      onNavigate={handleNavigate}
      onLogout={handleLogout}
    />
  );
}

export default ForesightApp;
